import { useCallback } from "react";

interface UsePasteImageAttachmentParams {
  vmAvailable: boolean;
  onAttachFiles: (files: FileList | File[]) => void | Promise<void>;
}

/**
 * Paste handler for chat-input textareas: when the clipboard contains
 * one or more images (e.g. a screenshot), forward them to `onAttachFiles`
 * with stable filenames and prevent the default text-paste so the
 * textarea doesn't end up with an empty/garbled string.
 *
 * No-op when the VM is unavailable (nowhere to upload to) or when the
 * clipboard contains no image items.
 */
export const usePasteImageAttachment = (params: UsePasteImageAttachmentParams) => {
  const { vmAvailable, onAttachFiles } = params;
  return useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (!vmAvailable) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageFiles: File[] = [];
      for (const item of items) {
        if (!item.type.startsWith("image/")) continue;
        const file = item.getAsFile();
        if (!file) continue;
        const ext = file.name.split(".").pop() ?? "png";
        const id = Math.random().toString(36).slice(2, 10);
        const renamed = new File([file], `pasted-${id}.${ext}`, { type: file.type });
        imageFiles.push(renamed);
      }
      if (imageFiles.length > 0) {
        e.preventDefault();
        void onAttachFiles(imageFiles);
      }
    },
    [vmAvailable, onAttachFiles],
  );
};
